// The only place this module talks to C2 (ops-dashboard engine). Every response
// is typed as the S8 envelope, so `meta` is never optional. Shapes mirror
// ops-dashboard main (engine/tabs.py, runs.py, control.py, main.py, errors.py)
// as of C2 v1.10.

import type { FreshnessMeta } from "@/lib/api";

export type { FreshnessMeta };

export type Enveloped<T> = { data: T; meta: FreshnessMeta };

export type RowStatus = "ok" | "below_min" | "unknown" | "no_sales" | "no_threshold";
export type StockFlag = "LAGGED" | "NOT_IN_XERO" | "STALE" | null;
export type Entity = "LTD" | "OU" | "LLC";

/** One row of the Stock tab artifact, exactly as C2 writes it (numbers are strings). */
export type StockRow = {
  sku: string;
  channel_location: string;
  channel: string;
  location: string;
  entity_id: string;
  row_kind: "retail" | "case";
  product: string;
  variant: string;
  name: string;
  asin: string | null;
  units: string | null;
  source: "spapi" | "xero";
  flag: StockFlag;
  read_at: string | null;
  inbound_units: string | null;
  velocity_7d: string | null;
  velocity_30d: string | null;
  velocity_used: string | null;
  days_of_stock: string | null;
  days_after_inbound: string | null;
  convertible_units: string | null;
  days_added: string | null;
  outage_date: string | null;
  final_outage_date: string | null;
  min_days: string | null;
  effective_min_days: string | null;
  status: RowStatus;
  velocity_stale: boolean;
  /** C2 v1.5 (JSON only): units of in-flight conversions arriving on this row and leaving it, booked or not;
   * null when no card names the row; absent on artifacts written before C2 v1.5. */
  inflight_in?: string | null;
  inflight_out?: string | null;
  /** C2 v1.7: the row's IRT verdict (a stock-out's, at pool grain) and the NEW ISSUE flag; absent on artifacts written before C2 v1.7. */
  irt?: IrtVerdict;
  new_issue?: boolean;
};

/** C2 v1.7 call I: `matched` an open issue covers the problem, `new` NEW ISSUE, `unlisted` absent from the feed (never raised),
 * `none` no problem on the row, `unchecked` the IRT was not read or the step is not set up. */
export type IrtCheck = "matched" | "new" | "unlisted" | "none" | "unchecked";

/** One row's verdict: the covering or created issue's page, link, title and STATUS, else null; `created` only on the run that made it. */
export type IrtVerdict = {
  check: IrtCheck;
  page_id: string | null;
  url: string | null;
  title: string | null;
  status: string | null;
  created: boolean;
};

/** C2 v1.7 `segments.irt`: the counts of the pass's cross-check and its #ops_main notice. Per artifact: the 16:00 pass rewrites
 * the Uptime artifact alone with a listings-only segment, so after 16:00 the two tabs carry different segments. */
export type IrtSegment =
  | {
      status: "ok" | "failed";
      read_at: string;
      open_issues: number;
      typed_issues: number;
      problems: number;
      matched: number;
      new: number;
      unlisted: number;
      created: number;
      notified: number;
      /** `sent: <n>`, `skipped: nothing new`, `skipped: not configured`, `skipped: IRT not read` or `failed: <code>`. */
      notice: string;
      error?: string;
      create_failures?: string[];
    }
  | { status: "skipped"; reason: string };

/** C2 v1.5: one Stock Movements card still in flight, as `segments.inflight.cards` carries it (quantities are strings). */
export type InflightCard = {
  page_id: string;
  status: string | null;
  account: string | null;
  from_sku: string | null;
  from_qty: string | null;
  to_sku: string | null;
  to_qty: string | null;
  booked: boolean;
  created: string | null;
  url: string;
};

/** C2 v1.5: the in-flight step; `skipped` while the engine has no Stock Movements token. */
export type InflightSegment =
  | { status: "ok"; read_at: string; conversions: number; booked: number; attributed: number; unattributed: string[]; cards: InflightCard[] }
  | { status: "failed"; read_at: string; error: string }
  | { status: "skipped"; reason: string };

export type SegmentState = "ok" | "partial" | "stale" | "failed";

/** C2 registry reseed amendment: only the keys that apply to the status. */
export type ReseedSegment =
  | { status: "ok"; inserted: number; updated: number; unchanged: number; retired: number; skipped: number }
  | { status: "skipped"; reason: string }
  | { status: "failed"; error: string };

/** C2 v1.3: the Xero snapshot the run asked for, or `skipped` when it asked for none. */
export type XeroRefreshSegment =
  | { status: "ok" | "partial" | "timeout" | "failed"; read_at: string | null; note: string | null }
  | { status: "skipped"; reason: string };

export type StockArtifact = {
  tab: "stock";
  run_id: string;
  run_started_at: string;
  run_date: string;
  trigger_type: string;
  window_end: string | null;
  status: "ok" | "partial" | "failed";
  segments: {
    // Optional: artifacts written before the reseed amendment and C2 v1.3 lack them.
    reseed?: ReseedSegment;
    xero_refresh?: XeroRefreshSegment;
    inflight?: InflightSegment;
    irt?: IrtSegment;
    /** C2 v1.9: the Orders step's segment rides every artifact of the run; absent before it. */
    orders?: OrdersSegment;
    /** C2 v1.10: the forecast step's segment; absent before it. */
    forecast?: ProductionSegment;
    registry: { status: SegmentState; rows: number };
    velocity: { status: SegmentState; window_end: string; unattributed?: number; carried_from: string | null; error?: string };
    stock: { status: SegmentState; amazon_source: "spapi" | "xero"; failures: Record<string, string> };
    snapshot: { status: SegmentState; stock_rows: number; velocity_rows: number };
    tabs: { status: SegmentState };
  };
  /** C2 v1.3: the snapshot `read_at` served per organisation; absent on older artifacts. */
  xero_read_at?: Partial<Record<Entity, string | null>>;
  rows: StockRow[];
  filters: {
    products: string[];
    variants: string[];
    locations: string[];
    channels: string[];
    channel_locations: string[];
  };
};

/** C2 v1.6 (system spec addendum 3): the five purchasability statuses; only `ACTIVE IN STOCK` is live (BR-001). */
export type UptimeStatus = "ACTIVE IN STOCK" | "ACTIVE OUT OF STOCK" | "SUPPRESSED" | "INACTIVE" | "UNKNOWN";

/** One row of the Uptime tab artifact: one result per SKU, channel-location and marketplace, plus the registry's naming fields. */
export type UptimeRow = {
  sku: string;
  channel_location: string;
  marketplace: string;
  status: UptimeStatus;
  /** Why the row is not live, or why it is live but hidden from search; null on a plain live row. */
  reason: string | null;
  discoverable: boolean | null;
  buyable: boolean | null;
  issues_json: string | null;
  /** When the feed read the listing; null when the feed carried no row for this pair. */
  read_at: string | null;
  channel: string;
  location: string;
  entity_id: string;
  product: string;
  variant: string;
  name: string;
  asin: string | null;
  amazon_sku: string | null;
  /** C2 v1.7: as on a Stock row; an `ACTIVE OUT OF STOCK` result reads `none` (its stock-out is the Stock row's, debt D7 there). */
  irt?: IrtVerdict;
  new_issue?: boolean;
};

/** C2 v1.6 `segments.uptime`: the pass's cutoff and its counts; `error` when the listing view was dark. */
export type UptimeSegment = {
  status: SegmentState | "skipped";
  since?: string;
  checked?: number;
  live?: number;
  not_live?: number;
  unknown?: number;
  error?: string;
  reason?: string;
};

/** The Uptime tab artifact (`tabs/uptime/latest.json`), written by every pass; counts are the engine's, shown as served. */
export type UptimeArtifact = {
  tab: "uptime";
  run_id: string;
  run_started_at: string;
  run_date: string;
  trigger_type: string;
  status: "ok" | "partial" | "failed";
  /** The afternoon pass records every step it did not run as `skipped`, so only `uptime` is read by name here. */
  segments: Record<string, { status: string; [key: string]: unknown }> & { uptime?: UptimeSegment; irt?: IrtSegment };
  /** The cutoff the pass trusted listing rows from (UTC instant), null when the step did not run. */
  since: string | null;
  tiles: { live: number; not_live: number; unknown: number; checked: number; combinations: number };
  rows: UptimeRow[];
  filters: { products: string[]; marketplaces: string[]; channel_locations: string[]; statuses: UptimeStatus[] };
};

export type UptimeTabResponse = { artifact: UptimeArtifact | null; latest_run: RunRow | null };

/** C2 v1.9 `segments.orders`: the read's counts (`ok`), its reason (`failed`), or why it did not run (`skipped`). */
export type OrdersSegment = {
  status: SegmentState | "skipped";
  window_start?: string;
  window_end?: string;
  orders?: number;
  rows?: number;
  value_pending?: number;
  no_items?: number;
  duplicates?: number;
  unattributed?: number;
  unknown_stores?: string[];
  no_threshold?: string[];
  latest_order_at?: string | null;
  error?: string;
  reason?: string;
};

/** One order in any of the three views; a rank is null outside its group's top 20, the value null while unpriced. */
export type OrderRow = {
  order_id: string;
  channel: string;
  location: string;
  channel_location: string;
  currency: string;
  created_at: string;
  value: string | null;
  units: number | null;
  value_rank: number | null;
  units_rank: number | null;
  over_threshold: boolean;
};

/** One channel-location and currency: the scope every ranking stays inside (C2 v1.9 call A). */
export type OrdersGroup = {
  channel_location: string;
  currency: string;
  threshold: string | null;
  orders: number;
  value_pending: number;
  over_threshold: number;
};

/** The Orders tab artifact (`tabs/orders/latest.json`), written by every 06:00 and manual run that read the orders. */
export type OrdersArtifact = {
  tab: "orders";
  run_id: string;
  run_started_at: string;
  run_date: string;
  trigger_type: string;
  status: "ok" | "partial" | "failed";
  window_start: string;
  window_end: string;
  top_n: number;
  thresholds: Record<string, string>;
  segment: OrdersSegment;
  tiles: { orders: number; over_threshold: number; value_pending: number };
  groups: OrdersGroup[];
  rows: OrderRow[];
  filters: { channels: string[]; channel_locations: string[]; currencies: string[] };
};

export type OrdersTabResponse = { artifact: OrdersArtifact | null; latest_run: RunRow | null };

/** C2 v1.10 `segments.forecast`: the sizing's counts (`ok`), its reason (`failed`), or why it did not run (`skipped`). */
export type ProductionSegment = {
  status: SegmentState | "skipped";
  horizon_start?: string;
  horizon_end?: string;
  groups?: number;
  sized_rows?: number;
  unsized_rows?: number;
  not_produced_groups?: number;
  plans?: number;
  unmatched_plans?: number;
  lines_to_produce?: number;
  error?: string;
  reason?: string;
};

/**
 * One month of one entity, product and variant (C2 v1.10 `Line`): the need, how much converts from cases first, how
 * much is produced, `qty` the cases of `sku` to produce (rounded up on the cumulative), and the plans arriving that month.
 */
export type ProductionLine = {
  forecast_line_id: string;
  entity_id: string;
  product: string;
  variant: string;
  sku: string;
  month: string;
  need_units: number;
  convert_units: number;
  produce_units: number;
  qty: number;
  plan_units: string;
};

export type PlanCount = "inbound" | "overdue" | "future" | "beyond_horizon" | "undated" | "unmatched";

/** One open Production COGS plan line (S19) and how the forecast counted it (C2 v1.10 `PlanUse`, call D). */
export type ProductionPlan = {
  production_key: string;
  entity_id: string;
  sku: string;
  qty: string;
  units: string;
  target_date: string | null;
  status: string;
  confirmed_with_factory: boolean;
  counts_as: PlanCount;
  month: string | null;
  product: string | null;
  variant: string | null;
};

/** A retail row the forecast could not size: no stock read (or a stale one), or no sales speed (C2 v1.10 call B). */
export type UnsizedRow = { sku: string; channel_location: string; reason: "no_stock" | "no_velocity" };

/** The Production Plan artifact (`tabs/production/latest.json`), written by every 06:00 and manual run whose forecast step succeeded. */
export type ProductionArtifact = {
  tab: "production";
  run_id: string;
  run_started_at: string;
  run_date: string;
  trigger_type: string;
  status: "ok" | "partial" | "failed";
  months: string[];
  segment: ProductionSegment;
  tiles: { open_productions: number; in_production: number; needed_no_plan: number };
  lines: ProductionLine[];
  plans: ProductionPlan[];
  unsized: UnsizedRow[];
  filters: { entities: string[]; products: string[] };
};

export type ProductionTabResponse = { artifact: ProductionArtifact | null; latest_run: RunRow | null };

export type RunStatus = "running" | "ok" | "partial" | "failed";

export type RunRow = {
  run_id: string;
  trigger_type: string;
  requested_by: string | null;
  started_at: string;
  finished_at: string | null;
  segments_run: string[] | null;
  status: RunStatus;
  note: string | null;
};

export type Step =
  | "reseed" | "registry" | "xero_refresh" | "velocity" | "stock" | "inflight" | "calc" | "uptime" | "irt" | "snapshot" | "orders" | "forecast" | "tabs";
export type StepState = "pending" | "running" | "ok" | "partial" | "stale" | "skipped" | "timeout" | "failed";
/** Empty when the engine holds no progress for the run (unknown id, or a restart since it began: C2 D3, C6 D6). */
export type Progress = Partial<Record<Step, StepState>>;

/** C2 v1.4: the Xero refresh flag rides the one request the Stock tab makes (C6 D12 closed). */
export type StockTabResponse = { artifact: StockArtifact | null; latest_run: RunRow | null; xero_refresh_configured: boolean };
export type RunResponse = { run: RunRow; progress: Progress };
export type RefreshSource = "xero";
export type RefreshResponse = { run_id: string; started: boolean; status: "running" };

/** One row of `GET /v1/control/registry`: C2 v1.4 `control.READ_COLUMNS` (the channel default and the row version, D9 and D10). */
export type RegistryRow = {
  sku: string;
  channel_location: string;
  row_kind: "retail" | "case";
  product: string;
  variant: string;
  name: string;
  asin: string | null;
  amazon_sku: string | null;
  case_sku: string | null;
  units_per_case: number | null;
  min_days: string | null;
  effective_min_days: string | null;
  channel: string;
  location: string;
  entity_id: string;
  is_pool: boolean;
  status: "active" | "inactive";
  /** The channel-location default as stored ("40.00", "60.00"), null on wholesale; the figure an override hides. */
  default_min_days: string | null;
  /** The row's content version; sent back verbatim on every save so a stale save is refused (D13). */
  updated_at: string;
};

export type RegistryEdit = {
  sku: string;
  channel_location: string;
  min_days?: string | null;
  status?: "active" | "inactive";
  reason?: string;
  /** The `updated_at` the row was read with, as served (never re-parsed through a Date: C2 compares the instant exactly). */
  updated_at?: string;
};

/** C2 v1.4 `409 stale_row`: the row as it stands now rides the refusal. */
export type StaleRowDetails = { sku: string; channel_location: string; updated_at: string; row: RegistryRow };

/** `changed` false: nothing was written and `audit_id`/`at` are null (C2 reseed amendment R.7). */
export type RegistryEditResponse = { audit_id: string | null; at: string | null; changed: boolean; row: RegistryRow };

/** C2's error body; FastAPI's own 422 carries `detail` instead. */
export type ApiErrorBody = { error?: string; message?: string; details?: Record<string, unknown>; detail?: unknown };

// Same-origin via the Pages Function proxy (functions/ops/[[path]].ts).
const BASE = import.meta.env.VITE_OPS_API_BASE ?? "/ops/v1";

const HEADERS = { Accept: "application/json", "Content-Type": "application/json" };

async function get<T>(path: string): Promise<Enveloped<T>> {
  const response = await fetch(`${BASE}${path}`, { credentials: "include", headers: HEADERS });
  if (!response.ok) throw new OpsError(response.status, await safeBody(response));
  return (await response.json()) as Enveloped<T>;
}

async function post<T>(path: string, body: unknown): Promise<Enveloped<T>> {
  const response = await fetch(`${BASE}${path}`, {
    method: "POST",
    credentials: "include",
    headers: HEADERS,
    body: JSON.stringify(body),
  });
  if (!response.ok) throw new OpsError(response.status, await safeBody(response));
  return (await response.json()) as Enveloped<T>;
}

async function safeBody(response: Response): Promise<ApiErrorBody | null> {
  try {
    return (await response.json()) as ApiErrorBody;
  } catch {
    return null;
  }
}

export class OpsError extends Error {
  constructor(
    readonly status: number,
    readonly body: ApiErrorBody | null,
  ) {
    super(`OPS API ${status}`);
  }
}

export const getStockTab = () => get<StockTabResponse>("/tabs/stock");
export const getUptimeTab = () => get<UptimeTabResponse>("/tabs/uptime");
export const getOrdersTab = () => get<OrdersTabResponse>("/tabs/orders");
export const getProductionTab = () => get<ProductionTabResponse>("/tabs/production");
export const getRun = (runId: string) => get<RunResponse>(`/runs/${encodeURIComponent(runId)}`);
export const startRefresh = (source?: RefreshSource) =>
  post<RefreshResponse>("/refresh", source ? { segments: ["stock"], source } : { segments: ["stock"] });
export const listRegistry = () => get<RegistryRow[]>("/control/registry");
export const editRegistry = (body: RegistryEdit) => post<RegistryEditResponse>("/control/registry", body);

// One place for the query keys, so no file imports `./index` and no import cycle forms.
export const STOCK_QUERY_KEY = ["ops", "tabs", "stock"] as const;
export const UPTIME_QUERY_KEY = ["ops", "tabs", "uptime"] as const;
export const ORDERS_QUERY_KEY = ["ops", "tabs", "orders"] as const;
export const PRODUCTION_QUERY_KEY = ["ops", "tabs", "production"] as const;
export const REGISTRY_QUERY_KEY = ["ops", "control", "registry"] as const;

/** C2 v1.4 D13: the save named a version the row has moved past; nothing was written. */
export const STALE_ROW =
  "This row changed since you opened it (another person, another tab, or the morning reseed), so nothing was saved. The current values are shown; apply your change again.";

/** The current row a `409 stale_row` refusal carries, or null for any other error. */
export function staleRow(error: unknown): RegistryRow | null {
  if (!(error instanceof OpsError) || error.status !== 409 || error.body?.error !== "stale_row") return null;
  const row = (error.body.details as Partial<StaleRowDetails> | undefined)?.row;
  return row && typeof row.sku === "string" ? row : null;
}

/** C2 v1.3 C6 note, verbatim. */
export const XERO_BUSY = "A run without a Xero refresh is in progress; press Refresh Xero stock again when it finishes.";

/**
 * One sentence per failure. A non-OpsError (network) and the unconfigured
 * proxy degrade to the same "nothing is shown" statement — never to a blank
 * or an empty table, which would assert "there is nothing".
 */
export function messageFor(error: unknown, areaName: string): string {
  const unreachable = "We can't reach the OPS engine, so nothing is shown. Try again shortly.";
  if (!(error instanceof OpsError)) return unreachable;
  const code = error.body?.error;
  const message = error.body?.message;
  // A database rule names itself (`invariant`: an `ops:` RAISE; `constraint`: a CHECK); show it as written.
  if ((code === "constraint" || code === "invariant") && message) return message;
  if (code === "run_in_progress_without_xero") return XERO_BUSY;
  if (code === "stale_row") return STALE_ROW;
  if (code === "xero_refresh_not_configured") return "Refresh Xero stock is not set up on the engine yet. Use Refresh instead.";
  if (code === "not_configured") return unreachable;
  switch (error.status) {
    case 401:
      return "Your sign-in has expired. Reload to sign in again.";
    case 403:
      // C2 mirrors the portal: `details.reason` is `denied` (a refusal) or
      // `resolver_unreachable` (a fault). A fault must never read as a refusal.
      return error.body?.details?.reason === "resolver_unreachable"
        ? "Permissions can't be checked right now, so nothing was changed. Try again shortly."
        : `You do not have permission here. Ask the ${areaName} area owner.`;
    case 404:
      return code === "run_unknown"
        ? "That run no longer exists. Reload to see the latest one."
        : "That row no longer exists in the registry. Reload.";
    case 409:
      return message ?? "That change clashes with the current data. Reload and try again.";
    case 422:
      // FastAPI's validation body has no `message`; its `detail` is not operator copy.
      return message ?? "Check the values: minimum days must be a whole number above zero, or blank to use the default.";
    case 503:
      return "The dashboard data can't be read right now, so nothing is shown. Try again shortly.";
    default:
      return "Something went wrong. Try again.";
  }
}
